import React, { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import Footer from "../components/Footer";
import { WaziWordmark, WaziMark } from "../components/Logo";
import { Button } from "../components/ui/button";
import { trackEvent } from "../lib/analytics";
import { toast } from "sonner";
import { Star, MapPin, Briefcase, MessageCircle, CheckCircle2, Globe, Languages, Gift, Share2, Copy } from "lucide-react";

const Stars = ({ value, size = 16 }) => (
  <div className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((s) => (
      <Star
        key={s}
        style={{ width: size, height: size }}
        className={s <= Math.round(value) ? "text-[#FFB400] fill-[#FFB400]" : "text-[#E2E8F0]"}
      />
    ))}
  </div>
);

const WorkerProfile = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/public/workers/${slug}`).then((r) => setData(r.data)).catch((e) => setErr(e?.response?.status === 404 ? "Not found" : "Error"));
  }, [slug]);

  // Track referral visit when ?ref=CODE is present
  useEffect(() => {
    const ref = searchParams.get("ref");
    if (!ref) return;
    api.post("/public/ref/visit", { ref }).catch(() => {});
    try { localStorage.setItem("wazi_ref", ref); } catch (e) {}
    trackEvent("referral_visit", { ref_code: ref, worker_slug: slug });
  }, [searchParams, slug]);

  if (err) return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center">
      <WaziWordmark size={40} />
      <h2 className="font-heading mt-6 text-2xl font-bold text-[#2D3748]">Worker not found</h2>
      <p className="mt-2 text-[#718096]">No worker exists with slug "{slug}".</p>
      <Link to="/" className="mt-6"><Button className="rounded-full">Back home</Button></Link>
    </div>
  );

  if (!data) return <div className="min-h-screen flex items-center justify-center text-[#718096]">Loading…</div>;

  const { worker, reviews, review_summary } = data;
  const waLink = `https://wa.me/${(worker.whatsapp_number || "").replace(/[^0-9]/g, "")}?text=Hi%20${encodeURIComponent(worker.business_name)}%2C%20I%20found%20you%20on%20Wazi.`;

  return (
    <div className="bg-white">
      <div className="bg-[#F7FAFC] border-b border-[#E2E8F0]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/"><WaziWordmark size={28} /></Link>
          <a href={waLink} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent("worker_whatsapp_click", { worker_slug: worker.slug, location: "header" })}>
            <Button data-testid="profile-cta-whatsapp" className="rounded-full bg-[#25D366] hover:bg-[#16A34A] text-white px-5 font-medium wazi-cta-glow text-sm">
              <MessageCircle className="w-4 h-4 mr-1" /> WhatsApp
            </Button>
          </a>
        </div>
      </div>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2">
            <div className="flex items-start gap-5">
              {worker.avatar_url ? (
                <img src={worker.avatar_url} alt={worker.business_name} className="w-24 h-24 rounded-2xl object-cover border border-[#E2E8F0]" />
              ) : (
                <div className="w-24 h-24 rounded-2xl bg-[#25D366]/10 flex items-center justify-center font-heading text-3xl text-[#25D366] font-bold">
                  {worker.business_name?.[0]}
                </div>
              )}
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#2D3748]" data-testid="profile-business-name">{worker.business_name}</h1>
                  {worker.is_verified && (
                    <span className="inline-flex items-center gap-1 bg-[#1E88E5]/10 text-[#1E88E5] text-xs px-2 py-1 rounded-full font-medium">
                      <CheckCircle2 className="w-3 h-3" /> Verified
                    </span>
                  )}
                </div>
                <div className="mt-2 flex items-center gap-3 flex-wrap text-sm">
                  <Stars value={worker.rating_avg || 0} />
                  <span className="text-[#2D3748] font-medium">{(worker.rating_avg || 0).toFixed(1)}</span>
                  <span className="text-[#718096]">· {worker.reviews_count || 0} reviews</span>
                  <span className="text-[#718096]">· {worker.jobs_completed || 0} jobs</span>
                </div>
                <div className="mt-3 flex items-center gap-4 text-sm text-[#718096] flex-wrap">
                  <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" /> {(worker.service_areas || []).join(", ") || "South Africa"}</span>
                  <span className="inline-flex items-center gap-1"><Briefcase className="w-4 h-4" /> {worker.years_experience} yrs experience</span>
                  {worker.languages?.length > 0 && (
                    <span className="inline-flex items-center gap-1"><Languages className="w-4 h-4" /> {worker.languages.join(", ")}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-8">
              <h2 className="font-heading font-semibold text-xl text-[#2D3748]">About</h2>
              <p className="mt-3 text-[#2D3748] font-sans leading-relaxed">{worker.description || "Professional painter on Wazi."}</p>
              {review_summary && (
                <div className="mt-5 p-4 bg-[#F7FAFC] border border-[#E2E8F0] rounded-xl text-sm text-[#2D3748]">
                  <div className="flex items-center gap-2 text-[#16A34A] font-heading font-semibold mb-1"><CheckCircle2 className="w-4 h-4" /> What customers say</div>
                  {review_summary}
                </div>
              )}
            </div>

            {(worker.gallery || []).length > 0 && (
              <div className="mt-10">
                <h2 className="font-heading font-semibold text-xl text-[#2D3748]">Recent work</h2>
                <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
                  {worker.gallery.map((url, i) => (
                    <div key={i} className="aspect-square rounded-xl overflow-hidden border border-[#E2E8F0] lift-on-hover bg-[#F7FAFC]" data-testid={`gallery-${i}`}>
                      <img src={url} alt="" loading="lazy" className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-10">
              <h2 className="font-heading font-semibold text-xl text-[#2D3748]">Reviews</h2>
              {reviews?.length === 0 ? (
                <p className="mt-3 text-[#718096]">No reviews yet.</p>
              ) : (
                <ul className="mt-4 space-y-4">
                  {reviews?.map((r) => (
                    <li key={r.id} className="bg-white border border-[#E2E8F0] rounded-xl p-5" data-testid={`review-${r.id}`}>
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="font-heading font-semibold text-[#2D3748]">{r.customer_name}</div>
                        <Stars value={r.stars} />
                      </div>
                      <p className="mt-2 text-[#2D3748] font-sans leading-relaxed">{r.text}</p>
                      <div className="mt-2 text-xs text-[#718096]">{new Date(r.created_at).toLocaleDateString()}</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* sticky CTA card */}
          <aside className="md:sticky md:top-6 h-fit">
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-sm">
              <div className="text-[#718096] text-sm">Hire {worker.business_name}</div>
              <div className="mt-1 font-heading text-2xl font-bold text-[#2D3748]">Get a free quote</div>
              <p className="mt-2 text-sm text-[#718096]">Message on WhatsApp and we'll come back to you with a quote within a few hours.</p>
              <a href={waLink} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent("worker_whatsapp_click", { worker_slug: worker.slug, location: "aside" })}>
                <Button data-testid="profile-aside-whatsapp" className="mt-5 w-full rounded-full bg-[#25D366] hover:bg-[#16A34A] text-white py-6 font-medium wazi-cta-glow text-base">
                  <MessageCircle className="w-5 h-5 mr-2" /> Message on WhatsApp
                </Button>
              </a>
              <div className="mt-4 flex items-center justify-center gap-2 text-xs text-[#718096]">
                <Globe className="w-3 h-3" /> wazi.chat/u/{worker.slug}
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Refer & earn airtime — viral footer */}
      {worker.referral_code && (() => {
        const referLink = `${window.location.origin}/u/${worker.slug}?ref=${worker.referral_code}`;
        const shareText = encodeURIComponent(
          `Hey 👋 I use Wazi to manage my painting business — leads, quotes, reviews, all on WhatsApp.\nJoin with my link and I earn R20 airtime: ${referLink}`
        );
        const waShare = `https://wa.me/?text=${shareText}`;
        const copyLink = () => {
          try {
            navigator.clipboard.writeText(referLink);
            toast.success("Link copied — paste it on WhatsApp!");
            trackEvent("referral_link_copied", { worker_slug: worker.slug });
          } catch (e) {
            toast.error("Couldn't copy. Long-press the link below.");
          }
        };
        return (
          <section className="bg-gradient-to-br from-[#25D366] to-[#16A34A] py-16" data-testid="referral-footer">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-10 items-center">
              <div className="text-white">
                <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur px-3 py-1.5 rounded-full text-xs font-heading font-semibold">
                  <Gift className="w-3 h-3" /> Refer & earn airtime
                </div>
                <h2 className="font-heading mt-4 text-3xl md:text-4xl font-bold leading-tight tracking-tight">
                  Know another hustler?<br/>Share Wazi, earn R20 airtime.
                </h2>
                <p className="mt-4 text-white/90 font-sans leading-relaxed">
                  Every painter, plumber, or handyman who signs up through your link puts <b>R20 airtime</b> in your pocket. No cap — refer 20 friends, earn R400.
                </p>
                <ul className="mt-5 space-y-2 text-white/90 text-sm">
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> R20 airtime per signup, instantly tracked</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Share your link on WhatsApp status (1-tap)</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> See your earnings in your Wazi profile</li>
                </ul>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-2xl">
                <div className="flex items-center gap-3">
                  <WaziMark size={36} />
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[#718096] font-heading font-semibold">{worker.business_name}'s link</div>
                    <div className="font-heading font-bold text-lg text-[#2D3748]">Code: {worker.referral_code}</div>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-[#F7FAFC] border border-[#E2E8F0] rounded-xl text-sm text-[#2D3748] break-all font-mono" data-testid="referral-link">
                  {referLink}
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <Button onClick={copyLink} data-testid="copy-ref-btn" variant="outline" className="rounded-full">
                    <Copy className="w-4 h-4 mr-2" /> Copy link
                  </Button>
                  <a href={waShare} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent("referral_share_whatsapp", { worker_slug: worker.slug })}>
                    <Button data-testid="share-ref-btn" className="w-full rounded-full bg-[#25D366] hover:bg-[#16A34A] text-white">
                      <Share2 className="w-4 h-4 mr-2" /> Share on WhatsApp
                    </Button>
                  </a>
                </div>
                <div className="mt-4 text-xs text-[#718096] text-center">
                  {worker.referral_signups || 0} signups · R{(worker.airtime_earned_zar || 0).toFixed(0)} earned
                </div>
              </div>
            </div>
          </section>
        );
      })()}

      <Footer />
    </div>
  );
};

export default WorkerProfile;
